/**
 * @fileoverview DayMonthInput Component
 *
 * A controlled input for selecting a day/month combination (no year).
 * Returns and accepts a "dd/mm" string format.
 */

import React from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";

const DAYS = Array.from({ length: 31 }, (_, i) => {
  const day = String(i + 1).padStart(2, "0");
  return { value: day, label: day };
});

const MONTHS = [
  { value: "01", label: "januari" },
  { value: "02", label: "februari" },
  { value: "03", label: "maart" },
  { value: "04", label: "april" },
  { value: "05", label: "mei" },
  { value: "06", label: "juni" },
  { value: "07", label: "juli" },
  { value: "08", label: "augustus" },
  { value: "09", label: "september" },
  { value: "10", label: "oktober" },
  { value: "11", label: "november" },
  { value: "12", label: "december" },
];

interface DayMonthInputProps {
  value?: string;
  onChange: (value: string) => void;
  isDisabled?: boolean;
  label?: string;
}

const DayMonthInput: React.FC<DayMonthInputProps> = ({
  value,
  onChange,
  isDisabled = false,
  label,
}) => {
  const [day, month] = value ? value.split("/") : ["", ""];

  const handleDayChange = (newDay: string) => {
    const currentMonth = month || "";
    if (currentMonth) {
      onChange(`${newDay}/${currentMonth}`);
    } else {
      onChange(`${newDay}/`);
    }
  };

  const handleMonthChange = (newMonth: string) => {
    const currentDay = day || "";
    if (currentDay) {
      onChange(`${currentDay}/${newMonth}`);
    } else {
      onChange(`/${newMonth}`);
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <Label className="text-foreground text-sm font-medium">{label}</Label>
      )}
      <div className="flex gap-2">
        <Select
          value={day || ""}
          onValueChange={handleDayChange}
          disabled={isDisabled}
        >
          <SelectTrigger className="w-24">
            <SelectValue placeholder="Dag" />
          </SelectTrigger>
          <SelectContent
            position="popper"
            sideOffset={4}
            align="start"
            className="max-h-[200px]"
          >
            {DAYS.map(({ value: dayValue, label: dayLabel }) => (
              <SelectItem key={dayValue} value={dayValue}>
                {dayLabel}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={month || ""}
          onValueChange={handleMonthChange}
          disabled={isDisabled}
        >
          <SelectTrigger className="flex-1">
            <SelectValue placeholder="Maand" />
          </SelectTrigger>
          <SelectContent
            position="popper"
            sideOffset={4}
            align="start"
            className="max-h-[200px]"
          >
            {MONTHS.map(({ value: monthValue, label: monthLabel }) => (
              <SelectItem key={monthValue} value={monthValue}>
                {monthLabel}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default DayMonthInput;
